import { DateTime } from "luxon";
import { salesReps } from "../constants/salesReps";
import type { Contact, ContactOwnerId, ContactStage, MarketingEmailStatus } from "./contacts";
import {
  resolveContactInventoryOwnerId,
  resolveContactInventoryStage,
  resolveContactMarketingEmailStatus,
  resolveContactMarketingEmailSuppressed,
  resolveContactSalesOwnerId,
  resolveContactSalesStage,
} from "./contacts";
import type { ContactCompany, ContactCompanyRole } from "./contactCompanies";

/**
 * Campaign channel. Only `email` is implemented in Phase 1; `sms` is reserved
 * so the collection does not need a type rewrite later (ticket §19).
 */
export type CampaignType = "email" | "sms";

export const CAMPAIGN_TYPES: CampaignType[] = ["email", "sms"];

export type CampaignStatus =
  | "draft"
  | "scheduled"
  | "sending"
  | "sent"
  | "paused"
  | "cancelled";

export const CAMPAIGN_STATUSES: CampaignStatus[] = [
  "draft",
  "scheduled",
  "sending",
  "sent",
  "paused",
  "cancelled",
];

export const CAMPAIGN_STATUS_LABEL: Record<CampaignStatus, string> = {
  draft: "Draft",
  scheduled: "Scheduled",
  sending: "Sending",
  sent: "Sent",
  paused: "Paused",
  cancelled: "Cancelled",
};

export const CAMPAIGN_TYPE_LABEL: Record<CampaignType, string> = {
  email: "Email",
  sms: "SMS",
};

/** How often a scheduled campaign sends. `none` is a one-shot. */
export type CampaignRepeat = "none" | "daily" | "weekly" | "monthly" | "yearly";

export const CAMPAIGN_REPEATS: CampaignRepeat[] = [
  "none",
  "daily",
  "weekly",
  "monthly",
  "yearly",
];

export const CAMPAIGN_REPEAT_LABEL: Record<CampaignRepeat, string> = {
  none: "Doesn't repeat",
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
  yearly: "Yearly",
};

export function parseCampaignRepeat(value: unknown): CampaignRepeat {
  if (
    value === "daily" ||
    value === "weekly" ||
    value === "monthly" ||
    value === "yearly" ||
    value === "none"
  ) {
    return value;
  }
  return "none";
}

export function parseSendOccurrence(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value) && value >= 0) {
    return Math.floor(value);
  }
  return 0;
}

export const MARKETING_CAMPAIGNS_COLLECTION = "marketing_campaigns";
export const MARKETING_AUDIENCES_COLLECTION = "marketing_audiences";
export const CAMPAIGN_RECIPIENTS_COLLECTION = "campaign_recipients";

/** Recipients processed per send-now API call (client loops until remaining is 0). */
export const MARKETING_SEND_BATCH_SIZE = 150;

/** Sentinel stored on owner filters for unclaimed contacts. */
export const AUDIENCE_UNASSIGNED_OWNER = "__unassigned__";

export type AudienceFilterField =
  | "stage"
  | "inventoryStage"
  | "salesOwner"
  | "inventoryOwner"
  | "state"
  | "terminal"
  | "lastActivityAt"
  | "marketingEmailStatus";

export const AUDIENCE_FILTER_FIELDS: AudienceFilterField[] = [
  "stage",
  "inventoryStage",
  "salesOwner",
  "inventoryOwner",
  "state",
  "terminal",
  "lastActivityAt",
  "marketingEmailStatus",
];

export const AUDIENCE_FILTER_FIELD_LABEL: Record<AudienceFilterField, string> = {
  stage: "Sales stage",
  inventoryStage: "Inventory stage",
  salesOwner: "Sales owner",
  inventoryOwner: "Inventory owner",
  state: "State",
  terminal: "Terminal",
  lastActivityAt: "Last activity date",
  marketingEmailStatus: "Marketing email status",
};

export type AudienceFilterOperator =
  | "eq"
  | "neq"
  | "in"
  | "before"
  | "after"
  | "olderThanDays";

export const AUDIENCE_FILTER_OPERATOR_LABEL: Record<AudienceFilterOperator, string> = {
  eq: "is",
  neq: "is not",
  in: "is any of",
  before: "before",
  after: "after",
  olderThanDays: "older than (days)",
};

export function operatorsForAudienceField(
  field: AudienceFilterField,
): AudienceFilterOperator[] {
  if (field === "lastActivityAt") return ["before", "after", "olderThanDays"];
  return ["eq", "neq", "in"];
}

export type AudienceFilterValue = string | number | string[];

export interface AudienceFilterRule {
  kind: "rule";
  id: string;
  field: AudienceFilterField;
  operator: AudienceFilterOperator;
  value: AudienceFilterValue;
}

export interface AudienceFilterGroup {
  kind: "group";
  id: string;
  combinator: "and" | "or";
  children: AudienceFilterNode[];
}

export type AudienceFilterNode = AudienceFilterRule | AudienceFilterGroup;

/**
 * Named saved segment. Filter rules stay live. Handpicked and excluded ids are
 * explicit and are copied onto a campaign when that audience is selected.
 */
export interface MarketingAudience {
  id: string;
  name: string;
  filter: AudienceFilterGroup;
  /** Added on top of filter matches. With no rules, the audience is only these ids. */
  includedContactIds?: string[];
  /** Removed after the filter/include union. */
  excludedContactIds?: string[];
  createdByUserId: string;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Marketing campaign. Audience eligibility is the stored filter tree, evaluated
 * against live CRM contacts at preview (Phase 1) and again at send (Phase 3).
 */
export interface MarketingCampaign {
  id: string;
  name: string;
  /** Channel. Phase 1 UI locks this to `email`. */
  type: CampaignType;
  fromName: string;
  fromEmail: string;
  /** Portal user ids whose emails become Reply-To. Sales department + owners. */
  replyToUserIds?: string[];
  subject: string;
  htmlBody: string;
  audienceFilter: AudienceFilterGroup;
  /** Optional saved-audience id. Inline {@link audienceFilter} is always the source of truth. */
  savedAudienceId?: string;
  /**
   * Contacts added on top of filter matches. If the filter has no rules and this
   * list is non-empty, the audience is only these contacts (not the whole CRM).
   */
  includedContactIds?: string[];
  /** Contacts removed after the filter/include union, before structural exclusions. */
  excludedContactIds?: string[];
  /** Inventory vehicles to insert at send time, in display order. */
  featuredVehicleIds?: string[];
  /**
   * How those trucks render. Missing means cards for 1–3 and a list for 4+.
   * Off-website trucks still render, but without a public link.
   */
  featuredVehicleLayout?: "cards" | "list";
  /** Hidden from the default campaign list. Does not delete recipient history. */
  archived?: boolean;
  status: CampaignStatus;
  scheduledAt: Date | null;
  /** `none` = one-shot. Daily/weekly/monthly/yearly re-queues after each send. */
  repeat?: CampaignRepeat;
  /** Increments after each completed recurring send. Recipient docs are keyed by this. */
  sendOccurrence?: number;
  /** Recipients that received a `mail` enqueue for this send. */
  sentCount?: number;
  /** Recipients that failed in this send (retryable). */
  failedCount?: number;
  /** Recipients who used the unsubscribe link after this campaign. */
  unsubscribedCount?: number;
  sentAt: Date | null;
  createdByUserId: string;
  createdAt: Date;
  updatedAt: Date;
}

export type CampaignRecipientSendStatus = "queued" | "sent" | "failed";

export const CAMPAIGN_RECIPIENT_SEND_STATUSES: CampaignRecipientSendStatus[] = [
  "queued",
  "sent",
  "failed",
];

/**
 * Per-contact snapshot written at send. Doc id is
 * {@link campaignRecipientDocId} so retries are idempotent.
 */
export interface CampaignRecipient {
  id: string;
  campaignId: string;
  contactId: string;
  email: string;
  status: CampaignRecipientSendStatus;
  error?: string;
  mailDocId?: string;
  unsubscribeToken: string;
  /** Which recurring send this row belongs to. `0` for one-shot / first send. */
  occurrence?: number;
  sentAt: Date | null;
  unsubscribedAt?: Date | null;
  createdAt: Date;
  updatedAt: Date;
}

export function campaignRecipientDocId(
  campaignId: string,
  contactId: string,
  occurrence = 0,
): string {
  const safeOccurrence =
    typeof occurrence === "number" && Number.isFinite(occurrence)
      ? Math.max(0, Math.floor(occurrence))
      : 0;
  if (safeOccurrence === 0) return `${campaignId}_${contactId}`;
  return `${campaignId}_${contactId}_${safeOccurrence}`;
}

const MARKETING_SCHEDULE_ZONE = "America/New_York";

/**
 * Next send time after `from` (the occurrence that just ran). Skips slots
 * that are already in the past so a late send does not dump a backlog.
 */
export function nextMarketingCampaignScheduledAt(
  from: Date,
  repeat: CampaignRepeat,
  now = new Date(),
): Date | null {
  const cadence = parseCampaignRepeat(repeat);
  if (cadence === "none") return null;
  let dt = DateTime.fromJSDate(from, { zone: MARKETING_SCHEDULE_ZONE });
  if (!dt.isValid) {
    dt = DateTime.fromJSDate(now, { zone: MARKETING_SCHEDULE_ZONE });
  }
  const bump = () => {
    if (cadence === "daily") dt = dt.plus({ days: 1 });
    else if (cadence === "weekly") dt = dt.plus({ weeks: 1 });
    else if (cadence === "yearly") dt = dt.plus({ years: 1 });
    else dt = dt.plus({ months: 1 });
  };
  bump();
  const nowDt = DateTime.fromJSDate(now, { zone: MARKETING_SCHEDULE_ZONE });
  while (dt <= nowDt) bump();
  return dt.toJSDate();
}

/** After a send finishes: stay `sent`, or re-queue the next recurring slot. */
export function marketingCampaignAfterSendFinish(input: {
  repeat?: CampaignRepeat | unknown;
  scheduledAt: Date | null;
  sendOccurrence?: number;
  now?: Date;
}): {
  status: "sent" | "scheduled";
  scheduledAt: Date | null;
  sendOccurrence: number;
} {
  const repeat = parseCampaignRepeat(input.repeat);
  const sendOccurrence = parseSendOccurrence(input.sendOccurrence);
  if (repeat === "none") {
    return {
      status: "sent",
      scheduledAt: input.scheduledAt,
      sendOccurrence,
    };
  }
  const now = input.now ?? new Date();
  const from =
    input.scheduledAt instanceof Date && !Number.isNaN(input.scheduledAt.getTime())
      ? input.scheduledAt
      : now;
  // Send now on a still-future slot should not skip that occurrence.
  const scheduledAt =
    from.getTime() > now.getTime()
      ? from
      : nextMarketingCampaignScheduledAt(from, repeat, now);
  return {
    status: "scheduled",
    scheduledAt,
    sendOccurrence: sendOccurrence + 1,
  };
}

export type CampaignRecipientOutcome = CampaignRecipientSendStatus | "unsubscribed";

export const CAMPAIGN_RECIPIENT_OUTCOME_LABEL: Record<CampaignRecipientOutcome, string> = {
  queued: "Queued",
  sent: "Sent",
  failed: "Failed",
  unsubscribed: "Unsubscribed",
};

export function resolveCampaignRecipientOutcome(
  recipient: Pick<CampaignRecipient, "status" | "unsubscribedAt">,
): CampaignRecipientOutcome {
  if (recipient.unsubscribedAt) return "unsubscribed";
  return recipient.status;
}

/** Percent string in American notation, or null when the rate cannot be computed. */
export function formatCampaignPercent(numerator: number, denominator: number): string | null {
  if (!Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator <= 0) {
    return null;
  }
  const pct = (numerator / denominator) * 100;
  return `${pct.toLocaleString("en-US", {
    maximumFractionDigits: 1,
    minimumFractionDigits: 0,
  })}%`;
}

export function emptyAudienceFilter(): AudienceFilterGroup {
  return { kind: "group", id: newAudienceNodeId(), combinator: "and", children: [] };
}

export function defaultAudienceRule(
  field: AudienceFilterField = "stage",
): AudienceFilterRule {
  const operator = operatorsForAudienceField(field)[0];
  let value: AudienceFilterValue = "";
  if (field === "stage" || field === "inventoryStage") value = "prospect";
  if (field === "marketingEmailStatus") value = "subscribed";
  if (field === "lastActivityAt" && operator === "olderThanDays") value = 90;
  return {
    kind: "rule",
    id: newAudienceNodeId(),
    field,
    operator,
    value,
  };
}

export function newAudienceNodeId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `aud_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Company + pickup-location joins needed to resolve State and Terminal.
 * State comes from the contact dropoff and from company business/dropoff
 * `pickup_locations` (not a first-class Contact field). Terminals live on
 * companies; contacts inherit them through membership.
 */
export interface AudienceEvalContext {
  companiesByContactId: Map<string, ContactCompany[]>;
  locationStateById: Map<string, string>;
  now?: Date;
}

export function buildAudienceEvalContext(input: {
  companies: ContactCompany[];
  pickupLocations: Array<{ id?: string; state?: string }>;
  now?: Date;
}): AudienceEvalContext {
  const companiesByContactId = new Map<string, ContactCompany[]>();
  for (const company of input.companies) {
    for (const member of company.members ?? []) {
      const contactId = member.contactId?.trim();
      if (!contactId) continue;
      const list = companiesByContactId.get(contactId);
      if (list) list.push(company);
      else companiesByContactId.set(contactId, [company]);
    }
  }
  const locationStateById = new Map<string, string>();
  for (const loc of input.pickupLocations) {
    const id = loc.id?.trim();
    const state = loc.state?.trim();
    if (!id || !state) continue;
    locationStateById.set(id, normalizeAudienceState(state));
  }
  return {
    companiesByContactId,
    locationStateById,
    now: input.now,
  };
}

const STATE_NAME_TO_ABBR: Record<string, string> = {
  alabama: "AL",
  alaska: "AK",
  arizona: "AZ",
  arkansas: "AR",
  california: "CA",
  colorado: "CO",
  connecticut: "CT",
  delaware: "DE",
  florida: "FL",
  georgia: "GA",
  hawaii: "HI",
  idaho: "ID",
  illinois: "IL",
  indiana: "IN",
  iowa: "IA",
  kansas: "KS",
  kentucky: "KY",
  louisiana: "LA",
  maine: "ME",
  maryland: "MD",
  massachusetts: "MA",
  michigan: "MI",
  minnesota: "MN",
  mississippi: "MS",
  missouri: "MO",
  montana: "MT",
  nebraska: "NE",
  nevada: "NV",
  "new hampshire": "NH",
  "new jersey": "NJ",
  "new mexico": "NM",
  "new york": "NY",
  "north carolina": "NC",
  "north dakota": "ND",
  ohio: "OH",
  oklahoma: "OK",
  oregon: "OR",
  pennsylvania: "PA",
  "rhode island": "RI",
  "south carolina": "SC",
  "south dakota": "SD",
  tennessee: "TN",
  texas: "TX",
  utah: "UT",
  vermont: "VT",
  virginia: "VA",
  washington: "WA",
  "west virginia": "WV",
  wisconsin: "WI",
  wyoming: "WY",
  "district of columbia": "DC",
};

export const US_STATE_ABBREVIATIONS = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY", "DC",
] as const;

export function normalizeAudienceState(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  const asName = STATE_NAME_TO_ABBR[trimmed.toLowerCase()];
  if (asName) return asName;
  return trimmed.toUpperCase();
}

function normalizeOwnerKey(ownerId: ContactOwnerId): string {
  if (ownerId === null || ownerId === undefined || ownerId === "") {
    return AUDIENCE_UNASSIGNED_OWNER;
  }
  return String(ownerId);
}

function asStringList(value: AudienceFilterValue): string[] {
  if (Array.isArray(value)) {
    return value.map((v) => String(v).trim()).filter(Boolean);
  }
  const single = String(value ?? "").trim();
  return single ? [single] : [];
}

function resolveContactStates(
  contact: Contact,
  ctx: AudienceEvalContext,
): string[] {
  const states = new Set<string>();
  const dropoff = contact.dropoffLocation?.state?.trim();
  if (dropoff) states.add(normalizeAudienceState(dropoff));
  const companies = ctx.companiesByContactId.get(contact.id) ?? [];
  for (const company of companies) {
    if (company.businessAddressId) {
      const st = ctx.locationStateById.get(company.businessAddressId);
      if (st) states.add(st);
    }
    for (const locId of company.dropoffLocationIds ?? []) {
      const st = ctx.locationStateById.get(locId);
      if (st) states.add(st);
    }
  }
  return [...states];
}

function resolveContactTerminalIds(
  contact: Contact,
  ctx: AudienceEvalContext,
): string[] {
  const ids = new Set<string>();
  const companies = ctx.companiesByContactId.get(contact.id) ?? [];
  for (const company of companies) {
    for (const terminalId of company.terminalIds ?? []) {
      const trimmed = terminalId.trim();
      if (trimmed) ids.add(trimmed);
    }
  }
  return [...ids];
}

function scalarMatches(
  actual: string,
  operator: AudienceFilterOperator,
  value: AudienceFilterValue,
  normalize: (v: string) => string = (v) => v,
): boolean {
  const expected = asStringList(value).map(normalize);
  const haystack = normalize(actual);
  if (operator === "eq") {
    if (expected.length === 0) return haystack === "";
    return expected.some((v) => v === haystack);
  }
  if (operator === "neq") {
    if (expected.length === 0) return haystack !== "";
    return expected.every((v) => v !== haystack);
  }
  if (operator === "in") {
    if (expected.length === 0) return false;
    return expected.includes(haystack);
  }
  return false;
}

function listMatches(
  actuals: string[],
  operator: AudienceFilterOperator,
  value: AudienceFilterValue,
  normalize: (v: string) => string = (v) => v,
): boolean {
  const expected = asStringList(value).map(normalize);
  const haystack = actuals.map(normalize).filter(Boolean);
  if (operator === "eq" || operator === "in") {
    if (expected.length === 0) return haystack.length === 0;
    return expected.some((v) => haystack.includes(v));
  }
  if (operator === "neq") {
    if (expected.length === 0) return haystack.length > 0;
    return expected.every((v) => !haystack.includes(v));
  }
  return false;
}

function evaluateLastActivity(
  lastActivityAt: Date | null,
  operator: AudienceFilterOperator,
  value: AudienceFilterValue,
  now: Date,
): boolean {
  if (operator === "olderThanDays") {
    const days = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(days) || days < 0) return false;
    if (!lastActivityAt) return true;
    const cutoff = now.getTime() - days * 24 * 60 * 60 * 1000;
    return lastActivityAt.getTime() <= cutoff;
  }
  if (!lastActivityAt) return false;
  const raw = Array.isArray(value) ? value[0] : value;
  const target = new Date(String(raw ?? ""));
  if (Number.isNaN(target.getTime())) return false;
  if (operator === "before") return lastActivityAt.getTime() < target.getTime();
  if (operator === "after") return lastActivityAt.getTime() > target.getTime();
  return false;
}

function evaluateRule(
  rule: AudienceFilterRule,
  contact: Contact,
  ctx: AudienceEvalContext,
): boolean {
  const now = ctx.now ?? new Date();
  switch (rule.field) {
    case "stage":
      return scalarMatches(resolveContactSalesStage(contact), rule.operator, rule.value);
    case "inventoryStage":
      return scalarMatches(
        resolveContactInventoryStage(contact),
        rule.operator,
        rule.value,
      );
    case "salesOwner":
      return scalarMatches(
        normalizeOwnerKey(resolveContactSalesOwnerId(contact)),
        rule.operator,
        rule.value,
      );
    case "inventoryOwner":
      return scalarMatches(
        normalizeOwnerKey(resolveContactInventoryOwnerId(contact)),
        rule.operator,
        rule.value,
      );
    case "marketingEmailStatus":
      return scalarMatches(
        resolveContactMarketingEmailStatus(contact),
        rule.operator,
        rule.value,
      );
    case "state":
      return listMatches(
        resolveContactStates(contact, ctx),
        rule.operator,
        rule.value,
        normalizeAudienceState,
      );
    case "terminal":
      return listMatches(resolveContactTerminalIds(contact, ctx), rule.operator, rule.value);
    case "lastActivityAt":
      return evaluateLastActivity(contact.lastActivityAt ?? null, rule.operator, rule.value, now);
    default:
      return false;
  }
}

/**
 * Empty AND groups match everyone (vacuous truth). Empty OR groups match no one.
 * Filters stay bound to live CRM records — never a frozen id list.
 */
export function evaluateAudienceFilter(
  node: AudienceFilterNode,
  contact: Contact,
  ctx: AudienceEvalContext,
): boolean {
  if (node.kind === "rule") return evaluateRule(node, contact, ctx);
  if (node.children.length === 0) return node.combinator === "and";
  if (node.combinator === "and") {
    return node.children.every((child) => evaluateAudienceFilter(child, contact, ctx));
  }
  return node.children.some((child) => evaluateAudienceFilter(child, contact, ctx));
}

export function countMatchingAudience(
  contacts: Contact[],
  filter: AudienceFilterNode,
  ctx: AudienceEvalContext,
): number {
  return contacts.reduce(
    (count, contact) => (evaluateAudienceFilter(filter, contact, ctx) ? count + 1 : count),
    0,
  );
}

function formatRuleValue(
  rule: AudienceFilterRule,
  labels?: AudienceFilterSummaryLabels,
): string {
  if (Array.isArray(rule.value)) return rule.value.filter(Boolean).join(", ");
  if (rule.field === "lastActivityAt" && rule.operator === "olderThanDays") {
    return `${rule.value} days`;
  }
  const raw = String(rule.value ?? "").trim();
  if (rule.field === "salesOwner" || rule.field === "inventoryOwner") {
    if (raw === AUDIENCE_UNASSIGNED_OWNER) return "Unassigned";
    return labels?.ownerNameById?.get(raw) || raw;
  }
  if (rule.field === "terminal") {
    return labels?.terminalNameById?.get(raw) || raw;
  }
  return raw;
}

/** Optional name lookups so list summaries show people and terminals, not ids. */
export interface AudienceFilterSummaryLabels {
  ownerNameById?: ReadonlyMap<string, string>;
  terminalNameById?: ReadonlyMap<string, string>;
}

/** Short human summary for campaign lists, e.g. "Prospect AND Emily". */
export function summarizeAudienceFilter(
  node: AudienceFilterNode,
  labels?: AudienceFilterSummaryLabels,
): string {
  if (node.kind === "rule") {
    const field = AUDIENCE_FILTER_FIELD_LABEL[node.field];
    const op = AUDIENCE_FILTER_OPERATOR_LABEL[node.operator];
    const value = formatRuleValue(node, labels);
    return value ? `${field} ${op} ${value}` : field;
  }
  if (node.children.length === 0) return "All contacts";
  const joiner = node.combinator === "and" ? " AND " : " OR ";
  const parts = node.children.map((child) => {
    const text = summarizeAudienceFilter(child, labels);
    return child.kind === "group" && child.children.length > 1 ? `(${text})` : text;
  });
  return parts.join(joiner);
}

export function cloneAudienceFilterNode(node: AudienceFilterNode): AudienceFilterNode {
  if (node.kind === "rule") {
    return {
      ...node,
      id: newAudienceNodeId(),
      value: Array.isArray(node.value) ? [...node.value] : node.value,
    };
  }
  return {
    ...node,
    id: newAudienceNodeId(),
    children: node.children.map(cloneAudienceFilterNode),
  };
}

export function cloneAudienceFilterGroup(group: AudienceFilterGroup): AudienceFilterGroup {
  const cloned = cloneAudienceFilterNode(group);
  if (cloned.kind === "group") return cloned;
  return emptyAudienceFilter();
}

export type AudienceExclusionReason =
  | "no_email"
  | "invalid_email"
  | "unsubscribed"
  | "suppressed"
  | "duplicate_email"
  | "manual_exclude";

export const AUDIENCE_EXCLUSION_REASONS: AudienceExclusionReason[] = [
  "no_email",
  "invalid_email",
  "unsubscribed",
  "suppressed",
  "duplicate_email",
  "manual_exclude",
];

export const AUDIENCE_EXCLUSION_REASON_LABEL: Record<AudienceExclusionReason, string> = {
  no_email: "No email",
  invalid_email: "Invalid email",
  unsubscribed: "Unsubscribed",
  suppressed: "Suppressed",
  duplicate_email: "Duplicate email",
  manual_exclude: "Excluded",
};

const MARKETING_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const AUDIENCE_ELIGIBILITY_SAMPLE_SIZE = 50;

export function isValidMarketingEmail(email: string): boolean {
  return MARKETING_EMAIL_REGEX.test(email.trim());
}

export function normalizeMarketingEmail(raw: string | undefined | null): string {
  return (raw ?? "").trim().toLowerCase();
}

/** Address used for a marketing send: primary if present, otherwise secondary. */
export function resolveContactMarketingSendEmail(contact: Contact): string {
  const primary = normalizeMarketingEmail(contact.email);
  if (primary) return primary;
  return normalizeMarketingEmail(contact.secondaryEmail);
}

export interface AudiencePreviewRow {
  id: string;
  name: string;
  email: string;
  reasons: AudienceExclusionReason[];
}

export interface AudienceEligibilityPreview {
  matchingCount: number;
  eligibleCount: number;
  excludedCount: number;
  excludedByReason: Record<AudienceExclusionReason, number>;
  eligibleSample: AudiencePreviewRow[];
  excludedSample: AudiencePreviewRow[];
}

function emptyExclusionCounts(): Record<AudienceExclusionReason, number> {
  return {
    no_email: 0,
    invalid_email: 0,
    unsubscribed: 0,
    suppressed: 0,
    duplicate_email: 0,
    manual_exclude: 0,
  };
}

export interface AudienceSelection {
  includedContactIds?: readonly string[];
  excludedContactIds?: readonly string[];
}

export function audienceSelectionFromCampaign(
  campaign: Pick<MarketingCampaign, "includedContactIds" | "excludedContactIds">,
): AudienceSelection {
  return {
    includedContactIds: campaign.includedContactIds,
    excludedContactIds: campaign.excludedContactIds,
  };
}

export function audienceFilterHasRules(node: AudienceFilterNode): boolean {
  if (node.kind === "rule") return true;
  return node.children.some((child) => audienceFilterHasRules(child));
}

function normalizeIdSet(ids: readonly string[] | undefined): Set<string> {
  const set = new Set<string>();
  for (const raw of ids ?? []) {
    const id = String(raw ?? "").trim();
    if (id) set.add(id);
  }
  return set;
}

/**
 * Filter matches, plus included ids. An empty filter with includes is only
 * those ids — not the vacuous "all contacts" match.
 */
export function resolveAudienceMatchingContacts(
  contacts: Contact[],
  filter: AudienceFilterNode,
  ctx: AudienceEvalContext,
  selection?: AudienceSelection,
): Contact[] {
  const included = normalizeIdSet(selection?.includedContactIds);
  const byId = new Map(contacts.map((contact) => [contact.id, contact]));
  const handpickOnly = included.size > 0 && !audienceFilterHasRules(filter);
  const matched: Contact[] = [];
  const seen = new Set<string>();
  const push = (contact: Contact | undefined) => {
    if (!contact || seen.has(contact.id)) return;
    seen.add(contact.id);
    matched.push(contact);
  };
  if (handpickOnly) {
    for (const id of included) push(byId.get(id));
  } else {
    for (const contact of contacts) {
      if (evaluateAudienceFilter(filter, contact, ctx)) push(contact);
    }
    for (const id of included) push(byId.get(id));
  }
  return matched.sort(sortMatchingContacts);
}

function sortMatchingContacts(a: Contact, b: Contact): number {
  const aTime = a.lastActivityAt?.getTime() ?? 0;
  const bTime = b.lastActivityAt?.getTime() ?? 0;
  if (aTime !== bTime) return bTime - aTime;
  const nameCmp = (a.name ?? "").localeCompare(b.name ?? "", "en");
  if (nameCmp !== 0) return nameCmp;
  return a.id.localeCompare(b.id);
}

function structuralExclusionReasons(contact: Contact): AudienceExclusionReason[] {
  const reasons: AudienceExclusionReason[] = [];
  const email = resolveContactMarketingSendEmail(contact);
  if (!email) reasons.push("no_email");
  else if (!isValidMarketingEmail(email)) reasons.push("invalid_email");
  if (resolveContactMarketingEmailStatus(contact) === "unsubscribed") {
    reasons.push("unsubscribed");
  }
  if (resolveContactMarketingEmailSuppressed(contact)) {
    reasons.push("suppressed");
  }
  return reasons;
}

/**
 * Filter match (live CRM) plus send eligibility. Unsubscribe and suppress always
 * exclude, even if the audience filter would include them. Duplicate emails keep
 * the most recently active matching contact.
 */
export function previewAudienceEligibility(
  contacts: Contact[],
  filter: AudienceFilterNode,
  ctx: AudienceEvalContext,
  selection?: AudienceSelection,
): AudienceEligibilityPreview {
  const excludedIds = normalizeIdSet(selection?.excludedContactIds);
  const matching = resolveAudienceMatchingContacts(contacts, filter, ctx, selection);

  const rows: AudiencePreviewRow[] = matching.map((contact) => ({
    id: contact.id,
    name: contact.name || "Unnamed contact",
    email: resolveContactMarketingSendEmail(contact),
    reasons: excludedIds.has(contact.id)
      ? ["manual_exclude"]
      : structuralExclusionReasons(contact),
  }));

  const claimedEmails = new Set<string>();
  for (const row of rows) {
    if (row.reasons.length > 0) continue;
    if (claimedEmails.has(row.email)) {
      row.reasons.push("duplicate_email");
      continue;
    }
    claimedEmails.add(row.email);
  }

  const excludedByReason = emptyExclusionCounts();
  const eligible: AudiencePreviewRow[] = [];
  const excluded: AudiencePreviewRow[] = [];
  for (const row of rows) {
    if (row.reasons.length === 0) {
      eligible.push(row);
      continue;
    }
    excluded.push(row);
    for (const reason of row.reasons) {
      excludedByReason[reason] += 1;
    }
  }

  return {
    matchingCount: matching.length,
    eligibleCount: eligible.length,
    excludedCount: excluded.length,
    excludedByReason,
    eligibleSample: eligible.slice(0, AUDIENCE_ELIGIBILITY_SAMPLE_SIZE),
    excludedSample: excluded.slice(0, AUDIENCE_ELIGIBILITY_SAMPLE_SIZE),
  };
}

/**
 * Live eligible contacts for a send. Same rules as {@link previewAudienceEligibility}:
 * filter match, then exclude no/invalid email, unsubscribed, suppressed, duplicate
 * email (keep most recently active).
 */
export function listEligibleAudienceContacts(
  contacts: Contact[],
  filter: AudienceFilterNode,
  ctx: AudienceEvalContext,
  selection?: AudienceSelection,
): Contact[] {
  const excludedIds = normalizeIdSet(selection?.excludedContactIds);
  const matching = resolveAudienceMatchingContacts(contacts, filter, ctx, selection).filter(
    (contact) => !excludedIds.has(contact.id),
  );
  const claimedEmails = new Set<string>();
  const eligible: Contact[] = [];
  for (const contact of matching) {
    if (structuralExclusionReasons(contact).length > 0) continue;
    const email = resolveContactMarketingSendEmail(contact);
    if (!email || claimedEmails.has(email)) continue;
    claimedEmails.add(email);
    eligible.push(contact);
  }
  return eligible;
}

export const MARKETING_PERSONALIZATION_TOKENS = [
  { token: "FirstName", label: "First name" },
  { token: "LastName", label: "Last name" },
  { token: "FullName", label: "Full name" },
  { token: "CompanyName", label: "Company name" },
  { token: "SalesOwner", label: "Sales owner" },
  { token: "InventoryOwner", label: "Inventory owner" },
  { token: "Email", label: "Email" },
  { token: "UnsubscribeUrl", label: "Unsubscribe URL" },
] as const;

export type MarketingPersonalizationToken =
  (typeof MARKETING_PERSONALIZATION_TOKENS)[number]["token"];

export function splitContactDisplayName(name: string): {
  firstName: string;
  lastName: string;
} {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: "", lastName: "" };
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };
  return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
}

export function resolveContactPrimaryCompanyName(
  contactId: string,
  companies: ContactCompany[],
): string {
  const trimmedId = contactId.trim();
  if (!trimmedId) return "";
  const memberships: Array<{ company: ContactCompany; role: ContactCompanyRole }> = [];
  for (const company of companies) {
    const member = (company.members ?? []).find((m) => m.contactId === trimmedId);
    if (!member) continue;
    memberships.push({ company, role: member.role });
  }
  memberships.sort((a, b) => a.company.name.localeCompare(b.company.name));
  const owner = memberships.find((entry) => entry.role === "owner");
  return (owner ?? memberships[0])?.company.name?.trim() ?? "";
}

export interface MarketingOwnerNameSource {
  id?: string | number;
  first_name?: string;
  last_name?: string;
  displayName?: string;
  name?: string;
  email?: string;
}

function displayNameFromUserLike(user: MarketingOwnerNameSource | undefined): string {
  if (!user) return "";
  const full = `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim();
  return full || user.displayName?.trim() || user.name?.trim() || user.email?.trim() || "";
}

export function resolveMarketingOwnerDisplayName(
  ownerId: ContactOwnerId,
  usersById: Map<string, MarketingOwnerNameSource>,
): string {
  if (ownerId === null || ownerId === undefined || ownerId === "") return "";
  const key = String(ownerId);
  const fromUsers = displayNameFromUserLike(usersById.get(key));
  if (fromUsers) return fromUsers;
  const fromReps = salesReps.find((rep) => String(rep.id) === key);
  if (fromReps) {
    return `${fromReps.first_name} ${fromReps.last_name}`.trim();
  }
  return "";
}

export interface MarketingPersonalizationValues {
  FirstName: string;
  LastName: string;
  FullName: string;
  CompanyName: string;
  SalesOwner: string;
  InventoryOwner: string;
  Email: string;
  UnsubscribeUrl: string;
}

export function buildMarketingPersonalizationValues(input: {
  contact: Contact;
  companies: ContactCompany[];
  usersById: Map<string, MarketingOwnerNameSource>;
  sendEmail: string;
  unsubscribeUrl: string;
}): MarketingPersonalizationValues {
  const { firstName, lastName } = splitContactDisplayName(input.contact.name ?? "");
  return {
    FirstName: firstName,
    LastName: lastName,
    FullName: (input.contact.name ?? "").trim(),
    CompanyName: resolveContactPrimaryCompanyName(input.contact.id, input.companies),
    SalesOwner: resolveMarketingOwnerDisplayName(
      resolveContactSalesOwnerId(input.contact),
      input.usersById,
    ),
    InventoryOwner: resolveMarketingOwnerDisplayName(
      resolveContactInventoryOwnerId(input.contact),
      input.usersById,
    ),
    Email: input.sendEmail,
    UnsubscribeUrl: input.unsubscribeUrl,
  };
}

const PERSONALIZATION_TOKEN_RE = /\{\{\s*([A-Za-z]+)\s*\}\}/g;

export function applyMarketingPersonalization(
  template: string,
  values: MarketingPersonalizationValues,
): string {
  const lookup: Record<string, string> = {
    firstname: values.FirstName,
    lastname: values.LastName,
    fullname: values.FullName,
    companyname: values.CompanyName,
    salesowner: values.SalesOwner,
    inventoryowner: values.InventoryOwner,
    email: values.Email,
    unsubscribeurl: values.UnsubscribeUrl,
  };
  return template.replace(PERSONALIZATION_TOKEN_RE, (match, rawKey: string) => {
    const key = String(rawKey ?? "").toLowerCase();
    if (Object.prototype.hasOwnProperty.call(lookup, key)) return lookup[key] ?? "";
    return match;
  });
}

/** Locked From until sales@ exists as a Workspace mailbox or alias. */
export const MARKETING_CAMPAIGN_FROM_EMAIL = "admin@optictruckworks.com";
export const MARKETING_CAMPAIGN_FROM_NAME = "Optic Truck Works";

export function parseReplyToUserIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const item of value) {
    if (typeof item !== "string") continue;
    const id = item.trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return ids;
}

export function resolveMarketingReplyToEmails(
  replyToUserIds: string[],
  usersById: Map<string, { email?: string | null }>,
): string[] {
  const emails: string[] = [];
  const seen = new Set<string>();
  for (const id of replyToUserIds) {
    const email = usersById.get(id)?.email?.trim().toLowerCase() ?? "";
    if (!email || seen.has(email)) continue;
    seen.add(email);
    emails.push(email);
  }
  return emails;
}

/** Gmail chip: RFC 8058 one-click unsubscribe. Query params stay on the URL. */
export function marketingListUnsubscribeHeaders(
  unsubscribeUrl: string,
): Record<string, string> {
  const url = unsubscribeUrl.trim();
  return {
    "List-Unsubscribe": `<${url}>`,
    "List-Unsubscribe-Post": "List-Unsubscribe=One-Click",
  };
}

export function formatMarketingFromHeader(fromName: string, fromEmail: string): string | undefined {
  const email = fromEmail.trim();
  if (!email) return undefined;
  const name = fromName.trim();
  if (!name) return email;
  const safeName = name.replace(/"/g, "");
  return `"${safeName}" <${email}>`;
}

const VALID_STAGES: ContactStage[] = [
  "prospect",
  "opportunity",
  "customer",
  "unqualified",
];

export function parseAudienceFilterNode(raw: unknown): AudienceFilterNode | null {
  if (!raw || typeof raw !== "object") return null;
  const v = raw as Record<string, unknown>;
  if (v.kind === "rule") {
    const field = v.field;
    if (typeof field !== "string" || !(AUDIENCE_FILTER_FIELDS as string[]).includes(field)) {
      return null;
    }
    const operator = v.operator;
    if (
      typeof operator !== "string" ||
      !operatorsForAudienceField(field as AudienceFilterField).includes(
        operator as AudienceFilterOperator,
      )
    ) {
      return null;
    }
    return {
      kind: "rule",
      id: typeof v.id === "string" && v.id ? v.id : newAudienceNodeId(),
      field: field as AudienceFilterField,
      operator: operator as AudienceFilterOperator,
      value: parseAudienceValue(v.value),
    };
  }
  if (v.kind === "group") {
    const combinator = v.combinator === "or" ? "or" : "and";
    const childrenRaw = Array.isArray(v.children) ? v.children : [];
    const children: AudienceFilterNode[] = [];
    for (const child of childrenRaw) {
      const parsed = parseAudienceFilterNode(child);
      if (parsed) children.push(parsed);
    }
    return {
      kind: "group",
      id: typeof v.id === "string" && v.id ? v.id : newAudienceNodeId(),
      combinator,
      children,
    };
  }
  return null;
}

function parseAudienceValue(raw: unknown): AudienceFilterValue {
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (Array.isArray(raw)) {
    return raw.map((entry) => String(entry)).filter((entry) => entry.length > 0);
  }
  if (typeof raw === "string") return raw;
  return "";
}

export function parseAudienceFilterGroup(raw: unknown): AudienceFilterGroup {
  const parsed = parseAudienceFilterNode(raw);
  if (parsed && parsed.kind === "group") return parsed;
  return emptyAudienceFilter();
}

export function isValidContactStage(value: string): value is ContactStage {
  return (VALID_STAGES as string[]).includes(value);
}

export type { MarketingEmailStatus };
